import type { Distance, SpatialRectangle } from "@pdfking/math"
import { Floor, Max, Scale, Subtract, Total } from "@pdfking/math"
import { ToPixel } from "@pdfking/quantity"
import type { ColorID, PageShape } from "@pdfking/renderer-api"
import { HorizontalAlign, PlotFunction, VerticalAlign } from "@pdfking/renderer-api"

import type { Measure, RandomStream } from "#internal"

import { Capitalize } from "./Capitalize"
import { LoremProse } from "./LoremWords"

// Fills `box` from its top with justified lorem paragraphs: first lines
// indented, last lines ragged and closed by a full stop. Answers the words and
// how far down the text went, the bottom of its last line
export const DrawProse = ({
  box,
  color,
  first_paragraph_indent,
  font_size,
  line_height,
  measure,
  random,
}: {
  box: SpatialRectangle<Distance>
  color: ColorID
  // Room left free at the start of a line of the first paragraph (a drop cap)
  first_paragraph_indent?: (line: number) => Distance
  font_size: Distance
  line_height: Distance
  measure: Measure
  random: RandomStream
}): [PageShape[], Distance] => {
  const width_of = (text: string) => measure(text, font_size)
  const shapes: PageShape[] = []
  const prose = LoremProse(random)
  let pending: string | undefined
  const next_word = () => {
    const word = pending ?? prose.next().value
    pending = undefined
    return word
  }
  const space = width_of(" ")
  // The top of the next line, going down
  let top = Total(box.y, box.height)
  const fits = (lines: number) => Subtract(top, Scale(line_height, lines)) >= box.y
  let paragraph = 0
  while (fits(1)) {
    const lines = 2 + Floor(random() * 7)
    for (let line = 0; line < lines && fits(1); line++) {
      const indent =
        paragraph === 0 && first_paragraph_indent
          ? first_paragraph_indent(line)
          : line === 0 && paragraph > 0
            ? Scale(font_size, 1.5)
            : Scale(font_size, 0)
      const last = line === lines - 1 || !fits(2)
      const width = Subtract(box.width, indent)
      const target = last ? Scale(width, 0.25 + random() * 0.6) : width
      // A paragraph opens a sentence, whatever the prose was in the middle of
      const words = [line === 0 ? Capitalize(next_word()) : next_word()]
      let used = width_of(words[0]!)
      while (true) {
        const word = next_word()
        const word_width = width_of(word)
        if (Total(used, space, word_width) > target) {
          pending = word
          break
        }
        words.push(word)
        used = Total(used, space, word_width)
      }
      if (last) words[words.length - 1] = `${words[words.length - 1]!.replace(/[.,]$/, "")}.`
      const widths = words.map(width_of)
      const gap = last
        ? space
        : Max(space, Scale(Subtract(width, Total(...widths)), 1 / Max(1, words.length - 1)))
      let x = Total(box.x, indent)
      words.forEach((text, i) => {
        shapes.push({
          enum: PlotFunction.PLOT_TEXT,
          font_size: ToPixel(font_size),
          horizontal_align: HorizontalAlign.LEFT,
          position: { height: font_size, width: widths[i]!, x, y: Subtract(top, font_size) },
          text,
          text_color: color,
          vertical_align: VerticalAlign.TOP,
        })
        x = Total(x, widths[i]!, gap)
      })
      top = Subtract(top, line_height)
    }
    paragraph++
  }
  return [shapes, top]
}
